import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Login as LoginIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { WhatsAppConfigService } from '../services/whatsappConfigService';
import type {
  EmbeddedSignupStartResponse,
  EmbeddedSignupCompletePayload,
} from '../types/whatsapp.types';
import {
  buildEmbeddedSignupOptions,
  getUserActivationSnapshot,
  type EmbeddedSignupLoginOptions,
} from './embeddedSignupOptions';
import {
  invokeEmbeddedSignupLogin,
  type EmbeddedSignupSdk,
  type MetaLoginResponse,
} from './embeddedSignupSdk';

interface MetaSdk extends EmbeddedSignupSdk {
  init: (options: Record<string, unknown>) => void;
  getLoginStatus?: (callback: (response: MetaLoginResponse) => void) => void;
}

export const buildEmbeddedSignupLoginOptions = (
  session: EmbeddedSignupStartResponse
): EmbeddedSignupLoginOptions => buildEmbeddedSignupOptions(session.embedded_signup_config_id);

declare global {
  interface Window {
    FB?: MetaSdk;
    fbAsyncInit?: () => void;
  }
}

interface SignupSessionData {
  waba_id: string;
  phone_number_id: string;
  business_id?: string;
}

type EmbeddedSignupEventName = 'FINISH' | 'ERROR' | 'CANCEL' | 'UNKNOWN';

interface EmbeddedSignupEvent {
  name: EmbeddedSignupEventName;
  sessionData: SignupSessionData | null;
}

interface EmbeddedSignupButtonProps {
  configId: number;
  onConnected: () => void | Promise<void>;
}

const allowedMetaOrigins = new Set([
  'https://www.facebook.com',
  'https://web.facebook.com',
  'https://business.facebook.com',
]);
// Reserve time for the completion request; never abandon a human flow after 45s.
const META_SESSION_EXPIRY_MARGIN_MS = 5 * 1000;
const META_MIN_START_WINDOW_MS = 30 * 1000;
const SESSION_EXPIRED_MESSAGE =
  'La session de connexion Meta a expiré. Fermez la fenêtre Meta, puis cliquez sur Réessayer.';
const sessionTimeRemaining = (session: EmbeddedSignupStartResponse): number => {
  const expiresAt = Date.parse(session.expires_at);
  return Number.isFinite(expiresAt)
    ? Math.max(0, expiresAt - Date.now() - META_SESSION_EXPIRY_MARGIN_MS)
    : 0;
};
const META_SESSION_DATA_TIMEOUT_MS = 30 * 1000;
const META_SDK_LOAD_TIMEOUT_MS = 15 * 1000;
const META_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const META_SDK_SCRIPT_ID = 'meta-jssdk';
const MAX_DIAGNOSTIC_ERROR_LENGTH = 240;

let sdkPromise: Promise<MetaSdk> | null = null;

const logEmbeddedSignup = (stage: string, details: Record<string, unknown> = {}) => {
  // Keep browser diagnostics useful without exposing OAuth, phone, or Meta asset data.
  console.info('[WhatsApp Embedded Signup]', stage, details);
};

const logSerializedEmbeddedSignup = (stage: string, details: Record<string, unknown>) => {
  // A single string remains readable when browser log collectors flatten object arguments.
  console.info(`[WhatsApp Embedded Signup] ${stage} ${JSON.stringify(details)}`);
};

const getSafeErrorDetails = (error: unknown): Record<string, unknown> => {
  const responseStatus = (error as { response?: { status?: unknown } })?.response?.status;

  return {
    errorType: error instanceof Error ? error.name : typeof error,
    ...(typeof responseStatus === 'number' ? { responseStatus } : {}),
  };
};

const sanitizeDiagnosticText = (value: string, maximumLength: number): string =>
  value
    .replace(
      /((?:^|[?&\s])(?:code|access_token|token|oauth_token)=)[^&\s]+/gi,
      '$1[redacted]'
    )
    .replace(/\bEAA[A-Za-z0-9_-]{20,}\b/g, '[redacted]')
    .slice(0, maximumLength);

const getSafeLoginErrorDetails = (error: unknown): Record<string, unknown> => {
  const errorMessage = error instanceof Error ? error.message : undefined;
  const errorCode = (error as { code?: unknown })?.code;
  const sanitizedMessage = errorMessage
    ? sanitizeDiagnosticText(errorMessage, MAX_DIAGNOSTIC_ERROR_LENGTH)
    : undefined;
  const sanitizedCode =
    typeof errorCode === 'string' || typeof errorCode === 'number'
      ? sanitizeDiagnosticText(String(errorCode), 64)
      : undefined;

  return {
    ...getSafeErrorDetails(error),
    ...(sanitizedMessage ? { errorMessage: sanitizedMessage } : {}),
    ...(sanitizedCode ? { errorCode: sanitizedCode } : {}),
  };
};

const initializeMetaSdk = (
  sdk: MetaSdk,
  session: EmbeddedSignupStartResponse,
  source: string
): MetaSdk => {
  if (typeof sdk.init !== 'function' || typeof sdk.login !== 'function') {
    throw new Error("Le formulaire Meta n'est pas disponible.");
  }

  sdk.init({
    appId: session.app_id,
    autoLogAppEvents: true,
    xfbml: true,
    version: session.graph_api_version,
  });
  logEmbeddedSignup('sdk_initialized', {
    source,
    sdkUrl: META_SDK_URL,
    graphApiVersion: session.graph_api_version,
    hasFbInit: true,
    hasFbLogin: true,
  });
  return sdk;
};

const loadMetaSdk = (session: EmbeddedSignupStartResponse): Promise<MetaSdk> => {
  if (window.FB) {
    return Promise.resolve(initializeMetaSdk(window.FB, session, 'existing_window_fb'));
  }
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    let settled = false;
    const existingScript = document.getElementById(META_SDK_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript || document.createElement('script');
    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error("Le formulaire Meta n'a pas pu être chargé."));
    }, META_SDK_LOAD_TIMEOUT_MS);

    const previousAsyncInit = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      try {
        previousAsyncInit?.();
      } catch (error) {
        logEmbeddedSignup('previous_fb_async_init_failed', getSafeErrorDetails(error));
      }

      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      logEmbeddedSignup('fb_async_init_called');

      if (!window.FB) {
        reject(new Error("Le formulaire Meta n'est pas disponible."));
        return;
      }

      try {
        resolve(initializeMetaSdk(window.FB, session, 'fbAsyncInit'));
      } catch (error) {
        reject(error);
      }
    };

    script.onload = () => {
      logEmbeddedSignup('sdk_script_load_event', { hasWindowFb: Boolean(window.FB) });
    };
    script.onerror = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      reject(new Error("Le formulaire Meta n'a pas pu être chargé."));
    };

    if (!existingScript) {
      script.id = META_SDK_SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = META_SDK_URL;
      logEmbeddedSignup('sdk_script_appending', { src: META_SDK_URL });
      document.body.appendChild(script);
    }
  });
  return sdkPromise;
};

export const parseEmbeddedSignupEvent = (event: MessageEvent): EmbeddedSignupEvent | null => {
  if (!allowedMetaOrigins.has(event.origin)) return null;

  try {
    const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return null;

    const name: EmbeddedSignupEventName = ['FINISH', 'ERROR', 'CANCEL'].includes(payload?.event)
      ? payload.event
      : 'UNKNOWN';
    if (name !== 'FINISH') {
      return { name, sessionData: null };
    }

    const data = payload.data;
    if (!data?.waba_id || !data?.phone_number_id) {
      return { name, sessionData: null };
    }

    return {
      name,
      sessionData: {
        waba_id: String(data.waba_id),
        phone_number_id: String(data.phone_number_id),
        business_id: data.business_id ? String(data.business_id) : undefined,
      },
    };
  } catch {
    return null;
  }
};

export const parseMetaMessage = (event: MessageEvent): SignupSessionData | null =>
  parseEmbeddedSignupEvent(event)?.sessionData || null;

export const EmbeddedSignupButton: React.FC<EmbeddedSignupButtonProps> = ({
  configId,
  onConnected,
}) => {
  const [signupSession, setSignupSession] = useState<EmbeddedSignupStartResponse | null>(null);
  const [isPreparing, setIsPreparing] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sdkRef = useRef<MetaSdk | null>(null);
  const activeAttemptCleanupRef = useRef<(() => void) | null>(null);
  const preparationGenerationRef = useRef(0);
  const lifecycleGenerationRef = useRef(0);

  const prepareSignup = async () => {
    const generation = ++preparationGenerationRef.current;
    setIsPreparing(true);
    setErrorMessage(null);
    setSignupSession(null);
    sdkRef.current = null;

    logEmbeddedSignup('prepare_started', {
      hasWindowFb: Boolean(window.FB),
      hasPendingSdkLoad: Boolean(sdkPromise),
    });

    try {
      const session = await WhatsAppConfigService.startEmbeddedSignup(configId);
      if (generation !== preparationGenerationRef.current) return;

      if (
        !session.app_id ||
        !session.graph_api_version ||
        !session.embedded_signup_config_id ||
        !session.session_id ||
        !session.nonce
      ) {
        throw new Error('La configuration Meta recue est incomplete.');
      }
      if (sessionTimeRemaining(session) < META_MIN_START_WINDOW_MS) {
        throw new Error(SESSION_EXPIRED_MESSAGE);
      }

      const sdk = await loadMetaSdk(session);
      if (generation !== preparationGenerationRef.current) return;
      if (sessionTimeRemaining(session) < META_MIN_START_WINDOW_MS) {
        throw new Error(SESSION_EXPIRED_MESSAGE);
      }

      logEmbeddedSignup('sdk_and_session_ready', {
        hasFbInit: typeof sdk.init === 'function',
        hasFbLogin: typeof sdk.login === 'function',
        hasEmbeddedSignupConfig: Boolean(session.embedded_signup_config_id),
        hasSignupSession: Boolean(session.session_id && session.nonce),
        graphApiVersion: session.graph_api_version,
      });
      logEmbeddedSignup('fb_init_completed');

      sdkRef.current = sdk;
      setSignupSession(session);
    } catch (error: any) {
      if (generation !== preparationGenerationRef.current) return;
      sdkPromise = null;
      sdkRef.current = null;
      setSignupSession(null);
      logEmbeddedSignup('prepare_failed', getSafeErrorDetails(error));
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "La connexion Meta n'est pas disponible pour le moment."
      );
    } finally {
      if (generation === preparationGenerationRef.current) setIsPreparing(false);
    }
  };

  useEffect(() => {
    lifecycleGenerationRef.current += 1;
    setIsConnecting(false);
    void prepareSignup();
    return () => {
      lifecycleGenerationRef.current += 1;
      preparationGenerationRef.current += 1;
      activeAttemptCleanupRef.current?.();
      activeAttemptCleanupRef.current = null;
    };
  }, [configId]);

  useEffect(() => {
    if (!signupSession || isConnecting) return;
    const timer = window.setTimeout(() => {
      setSignupSession(null);
      setErrorMessage(SESSION_EXPIRED_MESSAGE);
      logEmbeddedSignup('prepared_session_expired');
    }, sessionTimeRemaining(signupSession));
    return () => window.clearTimeout(timer);
  }, [signupSession, isConnecting]);

  const handleConnect = (clickEvent: React.MouseEvent<HTMLButtonElement>) => {
    const sdk = sdkRef.current;
    const hasSdk = Boolean(sdk);
    const hasFbInit = typeof sdk?.init === 'function';
    const hasFbLogin = typeof sdk?.login === 'function';
    const hasEmbeddedSignupConfig = Boolean(signupSession?.embedded_signup_config_id);
    const hasSignupSession = Boolean(signupSession?.session_id && signupSession?.nonce);

    logEmbeddedSignup('connect_preflight', {
      hasSdk,
      hasFbInit,
      hasFbLogin,
      hasEmbeddedSignupConfig,
      hasSignupSession,
      isConnecting,
      userActivation: getUserActivationSnapshot(clickEvent.nativeEvent),
    });

    if (
      !sdk ||
      !hasFbInit ||
      !hasFbLogin ||
      !signupSession ||
      !hasEmbeddedSignupConfig ||
      !hasSignupSession ||
      isConnecting ||
      Boolean(activeAttemptCleanupRef.current)
    ) {
      if (!isConnecting) {
        setErrorMessage("La connexion Meta n'est pas prête. Actualisez la page puis réessayez.");
      }
      return;
    }

    if (sessionTimeRemaining(signupSession) < META_MIN_START_WINDOW_MS) {
      logEmbeddedSignup('session_refresh_required');
      // Do not launch FB.login after an await: require a fresh user gesture.
      void prepareSignup();
      return;
    }

    const lifecycleGeneration = lifecycleGenerationRef.current;
    const isCurrentLifecycle = () => lifecycleGeneration === lifecycleGenerationRef.current;
    setIsConnecting(true);
    setErrorMessage(null);

    let loginCallbackReceived = false;
    let loginRequestTimedOut = false;
    let signupCancelledOrFailed = false;
    let sessionData: SignupSessionData | null = null;
    let sessionDataSettled = false;
    let resolveSessionData: (data: SignupSessionData | null) => void = () => undefined;
    let sessionTimeoutId: number | undefined;
    let loginTimeoutId: number | undefined;
    let attemptDisposed = false;
    let capturedPopup: Window | null = null;
    let disposeAttempt: () => void = () => undefined;
    const sessionDataPromise = new Promise<SignupSessionData | null>((resolve) => {
      resolveSessionData = resolve;
    });

    const settleSessionData = (data: SignupSessionData | null) => {
      if (sessionDataSettled) return;
      sessionDataSettled = true;
      if (sessionTimeoutId !== undefined) {
        window.clearTimeout(sessionTimeoutId);
        sessionTimeoutId = undefined;
      }
      resolveSessionData(data);
    };

    let messageListener: (event: MessageEvent) => void = () => undefined;

    const cleanup = () => {
      if (sessionTimeoutId !== undefined) {
        window.clearTimeout(sessionTimeoutId);
        sessionTimeoutId = undefined;
      }
      if (loginTimeoutId !== undefined) {
        window.clearTimeout(loginTimeoutId);
        loginTimeoutId = undefined;
      }
      window.removeEventListener('message', messageListener);
      if (activeAttemptCleanupRef.current === disposeAttempt) {
        activeAttemptCleanupRef.current = null;
      }
    };

    disposeAttempt = () => {
      attemptDisposed = true;
      loginRequestTimedOut = true;
      settleSessionData(null);
      cleanup();
    };

    activeAttemptCleanupRef.current = disposeAttempt;

    messageListener = (event: MessageEvent) => {
      const embeddedEvent = parseEmbeddedSignupEvent(event);
      if (!embeddedEvent) return;

      logEmbeddedSignup('embedded_signup_event', {
        event: embeddedEvent.name,
        hasSessionData: Boolean(embeddedEvent.sessionData),
      });

      if (embeddedEvent.name === 'CANCEL' || embeddedEvent.name === 'ERROR') {
        signupCancelledOrFailed = true;
        settleSessionData(null);
        cleanup();
        setIsConnecting(false);
        setErrorMessage(
          embeddedEvent.name === 'CANCEL'
            ? 'La connexion Meta a été annulée. Vous pouvez relancer le formulaire.'
            : 'Meta a rencontré un problème. Vous pouvez relancer le formulaire.'
        );
        return;
      }

      if (embeddedEvent.sessionData) {
        sessionData = embeddedEvent.sessionData;
        settleSessionData(sessionData);
      }
    };

    window.addEventListener('message', messageListener);
    const expireAttempt = () => {
      if (attemptDisposed || loginRequestTimedOut || signupCancelledOrFailed) return;
      loginRequestTimedOut = true;
      settleSessionData(null);
      cleanup();
      logEmbeddedSignup('fb_login_callback_timeout', {
        reason: 'session_expired',
        popupCaptured: Boolean(capturedPopup),
        popupClosed: capturedPopup ? capturedPopup.closed : null,
        activation: getUserActivationSnapshot(),
      });
      setIsConnecting(false);
      setSignupSession(null);
      setErrorMessage(SESSION_EXPIRED_MESSAGE);
    };
    loginTimeoutId = window.setTimeout(expireAttempt, sessionTimeRemaining(signupSession));

    const processLoginResponse = async (response: MetaLoginResponse) => {
      if (loginCallbackReceived) return;
      // Check the absolute deadline too: background-tab timers can be delayed.
      if (!attemptDisposed && isCurrentLifecycle() && sessionTimeRemaining(signupSession) <= 0) {
        expireAttempt();
      }
      if (attemptDisposed || loginRequestTimedOut || signupCancelledOrFailed) {
        logEmbeddedSignup('fb_login_callback_ignored', {
          reason: attemptDisposed
            ? 'attempt_disposed'
            : loginRequestTimedOut
              ? 'timeout'
              : 'signup_cancelled_or_failed',
        });
        return;
      }

      loginCallbackReceived = true;
      if (loginTimeoutId !== undefined) {
        window.clearTimeout(loginTimeoutId);
        loginTimeoutId = undefined;
      }

      const code = response.authResponse?.code;
      logEmbeddedSignup('fb_login_callback', {
        status: response.status || 'unknown',
        hasAuthorizationCode: Boolean(code),
      });

      if (!code) {
        settleSessionData(null);
        cleanup();
        setIsConnecting(false);
        setErrorMessage(
          "La connexion Meta n'a pas été terminée. Vous pouvez relancer le formulaire."
        );
        return;
      }

      if (!sessionData && !sessionDataSettled) {
        sessionTimeoutId = window.setTimeout(() => {
          logEmbeddedSignup('session_data_timeout');
          settleSessionData(null);
        }, Math.min(META_SESSION_DATA_TIMEOUT_MS, sessionTimeRemaining(signupSession)));
      }
      const completedSessionData = sessionData || (await sessionDataPromise);
      if (attemptDisposed || signupCancelledOrFailed || !isCurrentLifecycle()) return;
      if (sessionTimeRemaining(signupSession) <= 0) {
        expireAttempt();
        return;
      }
      cleanup();
      if (!completedSessionData) {
        setIsConnecting(false);
        setErrorMessage(
          "Meta n'a pas retourné les informations du numéro. Relancez le formulaire."
        );
        return;
      }

      const payload: EmbeddedSignupCompletePayload = {
        session_id: signupSession.session_id,
        nonce: signupSession.nonce,
        code,
        ...completedSessionData,
      };
      try {
        await WhatsAppConfigService.completeEmbeddedSignup(configId, payload);
        if (!isCurrentLifecycle()) return;
        logEmbeddedSignup('completion_succeeded');
        setSignupSession(null);
        await onConnected();
      } catch (error: any) {
        if (!isCurrentLifecycle()) return;
        logEmbeddedSignup('completion_failed', getSafeErrorDetails(error));
        const completionErrorMessage =
          error?.response?.data?.message ||
          "La connexion WhatsApp n'a pas pu être finalisée.";
        await prepareSignup();
        if (isCurrentLifecycle()) setErrorMessage(completionErrorMessage);
      } finally {
        if (isCurrentLifecycle()) setIsConnecting(false);
      }
    };

    const handleLoginResponse = (response: MetaLoginResponse): void => {
      void processLoginResponse(response).catch((error) => {
        if (!isCurrentLifecycle()) return;
        settleSessionData(null);
        cleanup();
        logSerializedEmbeddedSignup('fb_login_callback_processing_failed', {
          ...getSafeLoginErrorDetails(error),
        });
        setIsConnecting(false);
        setErrorMessage(
          "La réponse de Meta n'a pas pu être traitée. Vous pouvez relancer le formulaire."
        );
      });
    };

    try {
      const loginOptions = buildEmbeddedSignupLoginOptions(signupSession);
      logEmbeddedSignup('fb_login_invoking', {
        hasEmbeddedSignupConfig: Boolean(loginOptions.config_id),
        responseType: loginOptions.response_type,
        overrideDefaultResponseType: loginOptions.override_default_response_type,
        usesConfigSelectedFlow: !('featureType' in loginOptions.extras),
        embeddedSignupVersion: loginOptions.extras.version,
        sessionInfoVersion: loginOptions.extras.sessionInfoVersion,
        userActivation: getUserActivationSnapshot(clickEvent.nativeEvent),
      });

      capturedPopup = invokeEmbeddedSignupLogin(
        sdk,
        handleLoginResponse,
        loginOptions,
        ({ popupCreated, target }) => {
          logEmbeddedSignup('window_open_intercepted', {
            popupCreated,
            target,
          });
        }
      );
      logEmbeddedSignup('fb_login_invoked', {
        popupCaptured: Boolean(capturedPopup),
        userActivation: getUserActivationSnapshot(clickEvent.nativeEvent),
      });
    } catch (error) {
      loginCallbackReceived = true;
      settleSessionData(null);
      cleanup();
      logSerializedEmbeddedSignup('fb_login_threw', {
        ...getSafeLoginErrorDetails(error),
        activation: getUserActivationSnapshot(clickEvent.nativeEvent),
      });
      setIsConnecting(false);
      setErrorMessage(
        "Le formulaire Meta ne peut pas être ouvert. Vérifiez les autorisations du navigateur, puis réessayez."
      );
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          Connectez votre propre numéro WhatsApp
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Le formulaire sécurisé de Meta s’ouvrira ici. DigitalCloud360 réalisera ensuite le
          raccordement technique automatiquement.
        </Typography>
      </Box>
      {errorMessage && <Alert severity="warning">{errorMessage}</Alert>}
      <Button
        variant="contained"
        color="success"
        startIcon={isPreparing || isConnecting ? <CircularProgress size={18} /> : <LoginIcon />}
        onClick={handleConnect}
        disabled={isPreparing || isConnecting || !signupSession}
        sx={{ alignSelf: 'flex-start' }}
      >
        {isPreparing
          ? 'Préparation de Meta...'
          : isConnecting
            ? 'Connexion en cours...'
            : 'Connecter mon WhatsApp avec Meta'}
      </Button>
      {isConnecting && (
        <Button
          variant="text"
          disabled={!activeAttemptCleanupRef.current}
          onClick={() => {
            if (!activeAttemptCleanupRef.current) return;
            activeAttemptCleanupRef.current?.();
            setIsConnecting(false);
            setSignupSession(null);
            setErrorMessage('Tentative annulée. Fermez la fenêtre Meta avant de réessayer.');
            logEmbeddedSignup('attempt_cancelled_locally');
          }}
          sx={{ alignSelf: 'flex-start' }}
        >
          Annuler cette tentative
        </Button>
      )}
      {errorMessage && !isPreparing && !signupSession && (
        <Button
          variant="text"
          startIcon={<RefreshIcon />}
          onClick={prepareSignup}
          sx={{ alignSelf: 'flex-start' }}
        >
          Réessayer
        </Button>
      )}
    </Stack>
  );
};
